<script setup lang="ts">
// MDS import
import "@maersk-global/mds-components-core-button";
// Vue imports
import { storeToRefs } from "pinia";
import { computed } from "vue";

import { useEquipmentLocationStore } from "@/store/history/equipment_location_store";
import { useEquipmentSearchIdsStore } from "@/store/history/equipment_id_store";
import type { Terminal } from "@/interface/common/location_inteface";
import SearchResultErrorView from "@/components/common/SearchResultErrorView.vue";
import { useEquipmentHistoryStore } from "@/store/history/equipment_history_store";
import { useTerminalConfigurationStore } from "@/store/common/terminal_configuration_store";
import { useSearchIdsStore } from "@/store/common/search_id_store";
import { useGlobalStore } from "@/store/global";
import { appButtonClick } from "@/utils/analytics";

const globalStore = useGlobalStore();

const labels = {
  clear: globalStore.sitecoreData.common.clear,
  search: globalStore.sitecoreData.common.search
};

const searchIdStore = useSearchIdsStore();

// Use the search id store to get all the added ids
const equipmentSearchStore = useEquipmentSearchIdsStore();
//  Use the location id store to get selected terminal id
const locationStore = useEquipmentLocationStore();
const terminalConfigurationStore = useTerminalConfigurationStore();

//  Use the location id store to get selected terminal id
const equipmentHistoryStore = useEquipmentHistoryStore();
const { invalidIdDetail } = storeToRefs(equipmentHistoryStore);
// Fetch the import assets based on the search ids
const { equipmentSearchIds } = storeToRefs(equipmentSearchStore);
// Fetch the selected terminal id
const tempSelectedTerminal = computed((): Terminal => locationStore.tempLocation);
// Checking if there was any error
const errorCode = computed((): string => equipmentHistoryStore.error);
const regions = computed((): any => locationStore.filteredLocations);

const getTerminalDetailsById = (regions, terminalId) => {
  for (const region of regions) {
    for (const country of region.countries) {
      for (const terminal of country.terminals) {
        if (terminal.id.toLocaleLowerCase() === terminalId) {
          const selectedAnalyticsTerminalDetails = {
            region: region.name,
            country: country.name,
            terminalName: terminal.name,
            oneStreamCode: terminal.oneStreamCode,
            tabName: "Container Event History"
          };
          locationStore.updateAnayticsTerminalDetails(selectedAnalyticsTerminalDetails);
          return selectedAnalyticsTerminalDetails;
        }
      }
    }
  }
  return null; // Not found
};

const validateIds = (): void => {
  locationStore.setTerminal(locationStore.tempLocation);
  terminalConfigurationStore.fetchConfiguration(locationStore.terminalSelected.id.toLocaleLowerCase());
  searchIdStore.setTransactionType("CEH");
  const selectedTerminal = getTerminalDetailsById(regions.value, locationStore.tempLocation.id.toLocaleLowerCase());
  appButtonClick("Container Event History", "Search", selectedTerminal.oneStreamCode, {
    ...locationStore.anayticsTerminalDetails,
    searchedAssetIds: equipmentSearchIds.value
  });
  equipmentHistoryStore.resetHistory();

  equipmentHistoryStore.fetchEquipmentHistory();
};

// Clear the search ids
const clearSearchIds = (): void => {
  equipmentSearchStore.clearSearchIds();
  equipmentHistoryStore.resetHistory();
};

const resetError = (): void => {
  equipmentHistoryStore.resetError();
};
</script>

<template>
  <div v-if="invalidIdDetail.length > 0">
    <SearchResultErrorView :ids="invalidIdDetail" @close-event="resetError" />
  </div>

  <div v-if="errorCode">
    <mc-notification
      appearance="error"
      fit="small"
      icon="exclamation-octagon"
      closable
      data-test="error-message-container"
    >
      <span> {{ globalStore.sitecoreData.http_errors[errorCode] }}</span>
    </mc-notification>
  </div>

  <section class="search-actions item-inline action-buttons button-top-margin-15px">
    <mc-button
      data-test="clear-button"
      variant="outlined"
      fit="medium"
      appearance="neutral"
      :label="labels.clear"
      @click="clearSearchIds"
    >
    </mc-button>

    <mc-button
      :disabled="equipmentSearchIds.length <= 0 || !Object.keys(tempSelectedTerminal).length"
      data-test="search-button"
      fit="medium"
      :label="labels.search"
      :loading="equipmentHistoryStore.isLoading"
      @click="validateIds"
    >
    </mc-button>
  </section>
</template>
